import { useCallback, useEffect, useRef, useState } from 'react';
import { isCrossOriginIsolated, measureRefreshRate, measureTimerResolution, type RefreshEstimate } from '@/engine/Clock';
import { ID1_CARD_WIDTH_MM, pxPerMmFromCard, runInputProbe, visualAngleDeg, type InputProbeResult } from '@/engine/calibration';
import { getBridge } from '@/platform';
import { evaluate, overallVerdict, useApp, type CalibrationResult } from '@/state/store';
import { Button, Panel, Stat, VerdictPill } from '@/components/ui';

type Step = 'intro' | 'display' | 'input' | 'physical' | 'summary';

const STEP_LABEL: Record<Step, string> = {
  intro: '시작',
  display: '디스플레이',
  input: '입력',
  physical: '물리 크기',
  summary: '결과',
};

export function Calibration() {
  const [step, setStep] = useState<Step>('intro');
  const [refresh, setRefresh] = useState<RefreshEstimate | null>(null);
  const [timerRes, setTimerRes] = useState<number | null>(null);
  const [input, setInput] = useState<InputProbeResult | null>(null);
  const [cardPx, setCardPx] = useState(320);
  const [distanceMm, setDistanceMm] = useState(550);
  const setCalibration = useApp((s) => s.setCalibration);
  const go = useApp((s) => s.go);

  /* ── 디스플레이 측정 ─────────────────────────────── */
  const [measuring, setMeasuring] = useState(false);
  const runDisplay = useCallback(async () => {
    setMeasuring(true);
    // 타이머 해상도를 먼저 잰다. 동기 루프라 rAF 측정과 겹치면 안 된다.
    setTimerRes(measureTimerResolution());
    const est = await measureRefreshRate(1200);
    setRefresh(est);
    setMeasuring(false);
  }, []);

  /* ── 입력 프로브 ─────────────────────────────────── */
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [probeRunning, setProbeRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runProbe = useCallback(async () => {
    if (!refresh) return;
    setProbeRunning(true);
    setProgress(0);
    const el = targetRef.current;
    const result = await runInputProbe({
      trials: 12,
      frameMs: refresh.frameMs,
      // 화면 표현은 여기서 소유한다. 엔진은 시각을 모른다.
      render: (visible) => { if (el) el.style.opacity = visible ? '1' : '0'; },
      onProgress: (done, total) => setProgress(done / total),
    });
    if (el) el.style.opacity = '0';
    setInput(result);
    setProbeRunning(false);
  }, [refresh]);

  /* ── 요약 확정 ───────────────────────────────────── */
  const finish = useCallback(async () => {
    if (!refresh || timerRes === null) return;
    const device = await getBridge().getDeviceProfile().catch(() => null);
    const result: CalibrationResult = {
      measuredAt: new Date().toISOString(),
      refresh,
      timerResolutionMs: timerRes,
      crossOriginIsolated: isCrossOriginIsolated(),
      input,
      pxPerMm: pxPerMmFromCard(cardPx),
      viewingDistanceMm: distanceMm,
      device,
    };
    setCalibration(result);
    setStep('summary');
  }, [refresh, timerRes, input, cardPx, distanceMm, setCalibration]);

  const calibration = useApp((s) => s.calibration);

  return (
    <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-6 px-6 py-10">
      <Stepper current={step} />

      {step === 'intro' && (
        <Panel title="캘리브레이션">
          <div className="flex flex-col gap-4 text-[15px] leading-relaxed text-[#5D7066]">
            <p>
              세션을 시작하기 전에 이 기기가 <strong className="text-[var(--color-screen-fg)]">밀리초 단위 측정에
              쓸 만한지</strong> 확인한다. 세 가지를 잰다 — 디스플레이가 프레임을 일정하게 내보내는지,
              입력 이벤트가 지연 없이 도착하는지, 화면의 물리적 크기가 얼마인지.
            </p>
            <p className="text-[13.5px] text-[var(--color-screen-dim)]">
              측정 중에는 다른 앱과 탭을 닫아 주세요. 배경에서 도는 작업이 프레임을 먹으면
              그 기기는 실제보다 나쁘게 측정됩니다.
            </p>
            <div className="pt-1">
              <Button onClick={async () => {
                // 전체화면·절전차단은 사용자 제스처 안에서 호출해야 허용된다.
                const b = getBridge();
                await b.setImmersive(true);
                await b.keepAwake(true);
                setStep('display');
              }}>
                시작하기
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {step === 'display' && (
        <Panel title="1 · 디스플레이">
          <div className="flex flex-col gap-5">
            <p className="text-[15px] leading-relaxed text-[#5D7066]">
              1.2초 동안 프레임 간격을 모아 주사율과 변산을 추정한다. 자극 지속시간은 ms 가 아니라
              <strong className="text-[var(--color-screen-fg)]"> 프레임 수</strong>로 환산되므로,
              이 값이 이후 모든 타이밍의 기준이 된다.
            </p>
            {refresh ? (
              <>
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
                  <Stat label="주사율" value={`${refresh.hz} Hz`} hint={`원시 ${refresh.rawHz.toFixed(2)} Hz`} />
                  <Stat label="프레임 길이" value={`${refresh.frameMs.toFixed(2)} ms`} />
                  <Stat label="간격 SD" value={`${refresh.sdDeltaMs.toFixed(2)} ms`} />
                  <Stat label="드롭" value={`${refresh.droppedDuringProbe} / ${refresh.sampleCount}`} />
                </div>
                <FrameFitTable frameMs={refresh.frameMs} />
              </>
            ) : null}
            <div className="flex gap-3">
              <Button onClick={runDisplay} disabled={measuring}>
                {measuring ? '측정 중…' : refresh ? '다시 측정' : '측정 시작'}
              </Button>
              <Button variant="ghost" onClick={() => setStep('input')} disabled={!refresh}>
                다음
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {step === 'input' && (
        <Panel title="2 · 입력">
          <div className="flex flex-col gap-5">
            <p className="text-[15px] leading-relaxed text-[#5D7066]">
              사각형이 나타나면 <strong className="text-[var(--color-screen-fg)]">아무 키나 누르거나
              화면을 탭</strong>하세요. 12회 반복합니다. 미리 누르지 말고, 보고 나서 최대한 빨리.
            </p>
            <div
              className="relative grid h-56 place-items-center rounded border border-[#DFE7DF] bg-[var(--color-screen)]"
              aria-live="polite"
            >
              <div
                ref={targetRef}
                style={{ opacity: 0 }}
                className="h-24 w-24 rounded bg-[var(--color-screen-fg)]"
                aria-hidden="true"
              />
              {probeRunning ? (
                <span className="absolute bottom-3 font-mono text-[11px] text-[var(--color-screen-dim)]">
                  {Math.round(progress * 12)} / 12
                </span>
              ) : null}
            </div>
            {input ? (
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
                <Stat label="RT 중앙값" value={`${input.medianRtMs.toFixed(0)} ms`} />
                <Stat label="RT SD" value={`${input.sdRtMs.toFixed(0)} ms`} hint="기기 + 참가자" />
                <Stat
                  label="핸들러 지연"
                  value={`${input.meanHandlerDelayMs.toFixed(1)} ms`}
                  hint={`SD ${input.sdHandlerDelayMs.toFixed(1)} ms · 순수 기계값`}
                />
                <Stat label="선행반응 / 무응답" value={`${input.anticipations} / ${input.timeouts}`} />
              </div>
            ) : null}
            <div className="flex gap-3">
              <Button onClick={runProbe} disabled={probeRunning || !refresh}>
                {probeRunning ? '진행 중…' : input ? '다시 측정' : '측정 시작'}
              </Button>
              <Button variant="ghost" onClick={() => setStep('physical')}>
                {input ? '다음' : '건너뛰기'}
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {step === 'physical' && (
        <Panel title="3 · 물리 크기">
          <div className="flex flex-col gap-5">
            <p className="text-[15px] leading-relaxed text-[#5D7066]">
              신용카드(또는 같은 규격의 카드)를 화면에 대고, 아래 사각형의 가로 폭이 카드와
              같아지도록 맞추세요. 자극의 시각도를 세션 간 일정하게 유지하기 위한 값입니다.
            </p>
            <div className="flex flex-col items-start gap-4">
              <div
                className="rounded-md border-2 border-[var(--color-accent-lit)] bg-[#EFF5EC]"
                style={{ width: `${cardPx}px`, height: `${cardPx * (53.98 / ID1_CARD_WIDTH_MM)}px` }}
                aria-hidden="true"
              />
              <label className="flex w-full max-w-md flex-col gap-2">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--color-screen-dim)]">
                  카드 폭 맞추기
                </span>
                <input
                  type="range" min={140} max={720} step={1} value={cardPx}
                  onChange={(e) => setCardPx(Number(e.target.value))}
                  className="w-full accent-[var(--color-accent-lit)]"
                />
              </label>
              <label className="flex w-full max-w-md flex-col gap-2">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--color-screen-dim)]">
                  시청 거리 — {distanceMm} mm
                </span>
                <input
                  type="range" min={250} max={900} step={10} value={distanceMm}
                  onChange={(e) => setDistanceMm(Number(e.target.value))}
                  className="w-full accent-[var(--color-accent-lit)]"
                />
              </label>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <Stat label="해상도" value={`${pxPerMmFromCard(cardPx).toFixed(2)} px/mm`} />
                <Stat
                  label="자극쌍 이심률 예시"
                  value={`${visualAngleDeg(30, distanceMm).toFixed(2)}°`}
                  hint="중심에서 30 mm 떨어진 자극"
                />
                <Stat label="devicePixelRatio" value={String(window.devicePixelRatio)} />
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={finish}>결과 확정</Button>
              <Button variant="ghost" onClick={() => setStep('input')}>이전</Button>
            </div>
          </div>
        </Panel>
      )}

      {step === 'summary' && calibration ? <Summary c={calibration} onRestart={() => setStep('display')} onHome={() => go('home')} /> : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */

function Stepper({ current }: { current: Step }) {
  const order: Step[] = ['intro', 'display', 'input', 'physical', 'summary'];
  const idx = order.indexOf(current);
  return (
    <nav className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em]">
      {order.map((s, i) => (
        <span key={s} className="flex items-center gap-2">
          <span className={i <= idx ? 'text-[var(--color-accent-lit)]' : 'text-[#D2DFCE]'}>
            {STEP_LABEL[s]}
          </span>
          {i < order.length - 1 ? <span className="text-[#D9E3DB]">/</span> : null}
        </span>
      ))}
    </nav>
  );
}

/**
 * 표준 SOA 들이 이 기기의 프레임에 정수로 떨어지는지 보여준다.
 * 75 Hz 에서 500 ms 는 37.5 프레임이라 딱 떨어지지 않는다 — 그런 기기라는 사실을
 * 데이터를 모으기 전에 알아야 한다.
 */
function FrameFitTable({ frameMs }: { frameMs: number }) {
  const targets = [100, 250, 500, 1250, 1500];
  return (
    <div className="overflow-x-auto rounded border border-[#DFE7DF]">
      <table className="w-full min-w-[420px] text-left font-mono text-[12px] tabular-nums">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-[0.1em] text-[var(--color-screen-dim)]">
            <th className="px-3 py-2 font-normal">요청 (ms)</th>
            <th className="px-3 py-2 font-normal">프레임</th>
            <th className="px-3 py-2 font-normal">실제 (ms)</th>
            <th className="px-3 py-2 font-normal">오차</th>
          </tr>
        </thead>
        <tbody>
          {targets.map((ms) => {
            const raw = ms / frameMs;
            const frames = Math.max(1, Math.round(raw));
            const actual = frames * frameMs;
            const err = actual - ms;
            const exact = Math.abs(raw - frames) < 1e-6;
            return (
              <tr key={ms} className="border-t border-[#E5EBE3]">
                <td className="px-3 py-2 text-[var(--color-screen-fg)]">{ms}</td>
                <td className="px-3 py-2">{frames}</td>
                <td className="px-3 py-2">{actual.toFixed(2)}</td>
                <td className={`px-3 py-2 ${exact ? 'text-[var(--color-screen-dim)]' : 'text-[#986D31]'}`}>
                  {err >= 0 ? '+' : ''}{err.toFixed(2)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Summary({ c, onRestart, onHome }: { c: CalibrationResult; onRestart: () => void; onHome: () => void }) {
  const checks = evaluate(c);
  const overall = overallVerdict(checks);
  const headline =
    overall === 'pass' ? '이 기기는 세션 실행에 적합하다'
    : overall === 'warn' ? '실행은 가능하나 품질 저하가 있다'
    : '이 기기로 수집한 데이터는 신뢰하기 어렵다';

  return (
    <div className="flex flex-col gap-5">
      <Panel>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <VerdictPill v={overall} />
            <span className="font-mono text-[11px] text-[var(--color-screen-dim)]">
              {new Date(c.measuredAt).toLocaleString('ko-KR')}
            </span>
          </div>
          <h2 className="text-xl font-semibold text-[var(--color-screen-fg)]">{headline}</h2>
        </div>
      </Panel>

      <Panel title="점검 항목">
        <ul className="flex flex-col">
          {checks.map((k) => (
            <li key={k.id} className="flex flex-col gap-1 border-t border-[#E5EBE3] py-3 first:border-t-0 first:pt-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="text-[14.5px] text-[var(--color-screen-fg)]">{k.label}</span>
                <span className="flex items-center gap-3">
                  <span className="font-mono text-[13px] tabular-nums text-[#5D7066]">{k.value}</span>
                  <VerdictPill v={k.verdict} />
                </span>
              </div>
              <p className="text-[12.5px] leading-snug text-[var(--color-screen-dim)]">{k.note}</p>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="세션에 각인될 값">
        <pre className="overflow-x-auto font-mono text-[11.5px] leading-relaxed text-[#9FB0AF]">
{JSON.stringify(
  {
    refreshHz: c.refresh.hz,
    frameMs: Number(c.refresh.frameMs.toFixed(4)),
    frameSdMs: Number(c.refresh.sdDeltaMs.toFixed(3)),
    timerResolutionMs: Number(c.timerResolutionMs.toFixed(5)),
    crossOriginIsolated: c.crossOriginIsolated,
    handlerDelaySdMs: c.input ? Number(c.input.sdHandlerDelayMs.toFixed(2)) : null,
    simpleRtSdMs: c.input ? Number(c.input.sdRtMs.toFixed(1)) : null,
    pxPerMm: c.pxPerMm ? Number(c.pxPerMm.toFixed(3)) : null,
    viewingDistanceMm: c.viewingDistanceMm,
    platform: c.device?.platform ?? 'unknown',
    devicePixelRatio: c.device?.devicePixelRatio ?? window.devicePixelRatio,
  },
  null,
  2,
)}
        </pre>
      </Panel>

      <div className="flex gap-3">
        <Button variant="ghost" onClick={onRestart}>다시 측정</Button>
        <Button onClick={onHome}>완료</Button>
      </div>
    </div>
  );
}

/** 화면을 떠날 때 절전차단을 반드시 해제한다. */
export function useReleaseOnUnmount() {
  useEffect(() => () => { void getBridge().keepAwake(false); }, []);
}
